(function exposeFinderScoring(root, factory) {
  const scoring = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = scoring;
  } else {
    root.CourtFinderScoring = scoring;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function buildScoring() {
  "use strict";

  /*
    Previous model awarded flat points: preference +24, experience +16, budget
    +18. Every paddle satisfying all three answers therefore scored exactly 58,
    which produced a five-way tie with identical reasons and made the tool look
    broken. It also never touched the construction ratings the catalogue
    actually carries.

    This model scores continuously against those ratings, so two paddles that
    both "match" still separate on how well they match.
  */

  const AXES = ["power", "control", "spin", "forgiveness", "sweetSpot", "maneuverability"];

  // Weights per preferred feel. Each set sums to 1.
  const PREFERENCE_WEIGHTS = {
    Control: { control: 0.4, forgiveness: 0.2, sweetSpot: 0.15, spin: 0.1, maneuverability: 0.1, power: 0.05 },
    Power: { power: 0.4, spin: 0.2, sweetSpot: 0.15, control: 0.1, maneuverability: 0.1, forgiveness: 0.05 },
    "All-court": { control: 0.22, power: 0.22, spin: 0.18, forgiveness: 0.15, sweetSpot: 0.13, maneuverability: 0.1 },
  };

  // Experience nudges the weighting rather than adding a flat bonus: new players
  // benefit from forgiveness and a big sweet spot, advanced players from spin.
  const EXPERIENCE_BIAS = {
    Beginner: { forgiveness: 0.12, sweetSpot: 0.08 },
    Intermediate: {},
    Advanced: { spin: 0.08, power: 0.04 },
  };

  function num(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  function includes(source, value) {
    return String(source || "")
      .toLowerCase()
      .includes(String(value || "").toLowerCase());
  }

  function blendWeights(preference, experience) {
    const base = PREFERENCE_WEIGHTS[preference] || PREFERENCE_WEIGHTS["All-court"];
    const bias = EXPERIENCE_BIAS[experience] || {};
    const merged = {};
    AXES.forEach((axis) => {
      merged[axis] = (base[axis] || 0) + (bias[axis] || 0);
    });
    const total = AXES.reduce((sum, axis) => sum + merged[axis], 0) || 1;
    AXES.forEach((axis) => {
      merged[axis] = merged[axis] / total;
    });
    return merged;
  }

  function budgetCeiling(budget) {
    if (budget === "under-120") return 12000;
    if (budget === "120-160") return 16000;
    return Infinity;
  }

  function ratingsPresent(product) {
    return AXES.some((axis) => num(product[axis]) > 0);
  }

  function scoreProduct(product, answers) {
    const weights = blendWeights(answers.preference, answers.experience);
    const rated = ratingsPresent(product);

    // Weighted average of 0-10 ratings -> 0-100.
    let fit = 0;
    if (rated) {
      AXES.forEach((axis) => {
        fit += Math.min(10, Math.max(0, num(product[axis]))) * weights[axis];
      });
      fit *= 10;
    }

    // Catalogued for the stated experience level is a modest tiebreaker, not the
    // dominant term - it used to be worth more than the paddle's actual ratings.
    let skillBonus = 0;
    if (answers.experience && includes(product.skill, answers.experience)) skillBonus += 5;
    if (answers.preference && includes(product.style, answers.preference)) skillBonus += 4;

    const score = rated ? Math.max(1, Math.min(100, Math.round(fit + skillBonus))) : 0;
    return { product, score, rated, weights };
  }

  // Reasons are built AFTER ranking so they can reference the result set, e.g.
  // "the highest power in your results" - which is what makes them differ.
  function buildReasons(entry, entries, answers) {
    const { product, weights } = entry;
    const reasons = [];
    const label = {
      power: "Power",
      control: "Control",
      spin: "Spin",
      forgiveness: "Forgiveness",
      sweetSpot: "Sweet spot",
      maneuverability: "Manoeuvrability",
    };

    // "the highest of your matches" is only true when this paddle is the SOLE
    // holder of that maximum. Several paddles often share a rating, and claiming
    // it on every card at once reads as broken.
    const best = {};
    const bestIsUnique = {};
    AXES.forEach((axis) => {
      const values = entries.map((e) => num(e.product[axis]));
      best[axis] = Math.max(...values, 0);
      bestIsUnique[axis] = values.filter((v) => v === best[axis]).length === 1;
    });

    // Lead with the two axes that matter most for the chosen feel.
    const leading = [...AXES]
      .filter((axis) => num(product[axis]) > 0)
      .sort((a, b) => weights[b] - weights[a])
      .slice(0, 2);

    leading.forEach((axis) => {
      const value = num(product[axis]);
      const isBest = value === best[axis] && bestIsUnique[axis] && entries.length > 1;
      reasons.push(
        `${label[axis]} ${value}/10${isBest ? " — the highest of your matches" : ""}`,
      );
    });

    const core = num(product.coreThickness);
    if (core >= 15.5) {
      reasons.push(`${core} mm core — a thicker core favours control and dwell`);
    } else if (core > 0) {
      reasons.push(`${core} mm core — a thinner core favours pop and put-aways`);
    }

    if (product.faceMaterial) {
      reasons.push(`${product.faceMaterial} face`);
    }

    if (answers.experience && includes(product.skill, answers.experience)) {
      reasons.push(`Catalogued for ${String(answers.experience).toLowerCase()} players`);
    }

    if (!reasons.length) reasons.push("Available now and inside your budget");
    return reasons.slice(0, 4);
  }

  function rankProducts(products, answers, limit = 5) {
    const ceiling = budgetCeiling(answers.budget);
    const scored = products
      .filter((product) => product.available && Number(product.price) <= ceiling)
      .map((product) => scoreProduct(product, answers))
      .sort(
        (a, b) =>
          b.score - a.score ||
          Number(a.product.price) - Number(b.product.price) ||
          String(a.product.title).localeCompare(String(b.product.title)),
      )
      .slice(0, limit);

    return scored.map((entry) => ({
      product: entry.product,
      score: entry.score,
      rated: entry.rated,
      reasons: buildReasons(entry, scored, answers),
    }));
  }

  return { rankProducts, scoreProduct, blendWeights };
});
